// What a session replay hides while it records. Both rrweb recorders – the
// snippet's host-page recorder and the widget's standalone one – build their
// masking options here, so a business rule's privacy settings mean the same
// thing wherever the recording runs.
//
// Dependency-free on purpose: the snippet bundles this file into its tight
// main bundle. Keep imports out.

/**
 * How aggressively the session replay masks page content while recording.
 * Mirrors the `session_replay_privacy_mode` pgEnum.
 *
 *   mask_all       – every text node and every input value.
 *   mask_sensitive – every input value.
 *   mask_none      – nothing but password fields. A new business rule's
 *                    default.
 *
 * Every mode also masks the rule's sensitive selectors and the elements a
 * host page flags itself with `weloop-mask` / `data-weloop-mask`.
 */
export type ReplayPrivacyMode = "mask_all" | "mask_none" | "mask_sensitive";

export const REPLAY_PRIVACY_MODES: readonly ReplayPrivacyMode[] = [
	"mask_all",
	"mask_sensitive",
	"mask_none",
];

/** A business rule's session-replay privacy, as the recorders consume it. */
export interface ReplayPrivacySettings {
	readonly privacyMode: ReplayPrivacyMode;
	// Administrator-authored CSS selectors whose text and values are masked
	// in every mode.
	readonly sensitiveSelectors: readonly string[];
}

/**
 * Used when no business rule applies – the rolling buffer, a composer
 * recording, or a start message without settings. Stricter than a new rule's
 * `mask_none` on purpose: nobody chose to record these unmasked.
 */
export const DEFAULT_REPLAY_PRIVACY: ReplayPrivacySettings = {
	privacyMode: "mask_sensitive",
	sensitiveSelectors: [],
};

// Elements the host page flags as sensitive on its own.
const HOST_MASK_SELECTOR = ".weloop-mask, [data-weloop-mask]";

/**
 * Everything the host page flags to keep away from Weloop: masked text and
 * blocked elements, in both the class and the `data-*` form. The snippet's
 * page context (the page text sent to the widget and its assistant) skips
 * these too, so content hidden from a replay is never sent another way.
 */
export const HOST_EXCLUDED_SELECTOR = `${HOST_MASK_SELECTOR}, .weloop-block, [data-weloop-block]`;

/** The slice of a DOM element the masking decision reads. */
export interface MaskableElement {
	readonly closest: (selector: string) => unknown;
	readonly matches: (selector: string) => boolean;
}

/** The rrweb `record()` options that carry the masking policy. */
export interface ReplayMaskingOptions {
	readonly maskAllInputs: true;
	readonly maskInputFn: (value: string, element: MaskableElement) => string;
	readonly maskTextClass: string;
	readonly maskTextSelector: string;
}

export const isReplayPrivacyMode = (
	value: unknown,
): value is ReplayPrivacyMode =>
	REPLAY_PRIVACY_MODES.some((mode) => mode === value);

/**
 * Read privacy settings off an untrusted value, such as a `postMessage`
 * payload or a `sessionStorage` entry. Anything malformed falls back to the
 * default, which masks every input.
 */
export const parseReplayPrivacy = (raw: unknown): ReplayPrivacySettings => {
	if (typeof raw !== "object" || raw === null) {
		return DEFAULT_REPLAY_PRIVACY;
	}
	const privacyMode = "privacyMode" in raw ? raw.privacyMode : undefined;
	const sensitiveSelectors =
		"sensitiveSelectors" in raw ? raw.sensitiveSelectors : undefined;
	return {
		privacyMode: isReplayPrivacyMode(privacyMode)
			? privacyMode
			: DEFAULT_REPLAY_PRIVACY.privacyMode,
		sensitiveSelectors: Array.isArray(sensitiveSelectors)
			? sensitiveSelectors.filter(
					(selector): selector is string =>
						typeof selector === "string" && selector.trim().length > 0,
				)
			: [],
	};
};

const maskValue = (value: string): string => "*".repeat(value.length);

// rrweb tags a "show password" field with `data-rr-is-password`, so it stays
// recognizable after its type flips to text.
const PASSWORD_SELECTOR = '[type="password" i], [data-rr-is-password]';

const isWithin = (element: MaskableElement, selector: string): boolean => {
	try {
		return element.closest(selector) !== null;
	} catch {
		return false;
	}
};

/**
 * Build the rrweb masking options for a rule's privacy settings.
 *
 * One invalid selector inside the combined `maskTextSelector` makes
 * `closest()` throw, and rrweb swallows the error by masking no text at all.
 * `isValidSelector` checks each one on its own so an invalid selector drops
 * out alone. This package has no DOM, so the browser caller supplies it.
 */
export const replayMaskingOptions = (
	settings: ReplayPrivacySettings,
	isValidSelector: (selector: string) => boolean,
): ReplayMaskingOptions => {
	const sensitive = settings.sensitiveSelectors
		.map((selector) => selector.trim())
		.filter((selector) => selector.length > 0 && isValidSelector(selector));
	const sensitiveSelector = [HOST_MASK_SELECTOR, ...sensitive].join(", ");
	const { privacyMode } = settings;
	return {
		// rrweb only calls `maskInputFn` for input kinds it was told to mask.
		// Flagging them all hands every value to `maskInputFn`, which decides
		// per element and leaves a value alone when nothing applies.
		maskAllInputs: true,
		maskInputFn: (value, element) => {
			const isMasked =
				privacyMode !== "mask_none" ||
				element.matches(PASSWORD_SELECTOR) ||
				isWithin(element, sensitiveSelector);
			return isMasked ? maskValue(value) : value;
		},
		maskTextClass: "weloop-mask",
		// `*` matches every element, so every text node is masked.
		maskTextSelector: privacyMode === "mask_all" ? "*" : sensitiveSelector,
	};
};
